import { BoxProps } from '@leagueplatform/genesis-commons';
import { useIntl } from '@leagueplatform/locales';
import type { Link } from 'types/types';
import { EXTERNAL_LINKS } from 'common/links';
import { APP_MANAGED_ROUTES } from 'common/app-managed-routes';
import { QUERY_PARAM_KEYS, QUERY_PARAM_MODAL_VALUE } from 'common/constants';
import { useUrlParam } from '../../hooks/use-url-param/use-url-param.hook';

export type NavMenuOptions = {
  heading?: string;
  items: Link[];
};

const mediumTextLink: BoxProps = {
  color: 'onSurface.text.primary',
  fontWeight: 'medium',
  fontFamily: 'inherit',
};

const mediumTextCriticalLink: BoxProps = {
  color: 'onSurface.text.critical',
  fontWeight: 'medium',
};

export function useUserSettingsLinksConfig() {
  const { formatMessage } = useIntl();
  const { setParam } = useUrlParam(QUERY_PARAM_KEYS.MODAL);
  const userSettingsLinks = [
    {
      heading: formatMessage({ id: 'STR_ACCOUNT' }),
      items: [
        {
          text: formatMessage({ id: 'STR_PERSONAL_INFO' }),
          to: APP_MANAGED_ROUTES.PERSONAL_INFO,
          linkProps: mediumTextLink,
          color: '$onSurfaceTextPrimary',
        },
        {
          text: formatMessage({ id: 'STR_ACCOUNT_SETTINGS' }),
          to: EXTERNAL_LINKS.ALBERTA_ACCOUNT_SETTINGS,
          linkProps: mediumTextLink,
          color: '$onSurfaceTextPrimary',
        },
        {
          text: formatMessage({ id: 'STR_ACCOUNT_VERIFICATION' }),
          to: EXTERNAL_LINKS.ALBERTA_ACCOUNT_VERIFICATION,
          linkProps: mediumTextLink,
          color: '$onSurfaceTextPrimary',
        },
        {
          text: formatMessage({ id: 'STR_NOTIFICATIONS' }),
          to: APP_MANAGED_ROUTES.NOTIFICATIONS,
          linkProps: mediumTextLink,
          color: '$onSurfaceTextPrimary',
        },
      ],
    },
    {
      heading: formatMessage({ id: 'STR_HELP_CENTRE' }),
      items: [
        {
          text: formatMessage({ id: 'STR_FIND_SUPPORT' }),
          to: APP_MANAGED_ROUTES.FIND_SUPPORT,
          color: '$onSurfaceTextPrimary',
          linkProps: mediumTextLink,
        },
      ],
    },
    {
      heading: formatMessage({ id: 'STR_ABOUT' }),
      items: [
        {
          text: formatMessage({ id: 'STR_MY_HEALTH_ALBERTA' }),
          to: APP_MANAGED_ROUTES.ABOUT_MY_HEALTH_ALBERTA,
          linkProps: mediumTextLink,
          color: '$onSurfaceTextPrimary',
        },
        {
          text: formatMessage({ id: 'STR_PRIVACY_POLICY' }),
          to: APP_MANAGED_ROUTES.PRIVACY_POLICY,
          linkProps: mediumTextLink,
          color: '$onSurfaceTextPrimary',
        },
        {
          text: formatMessage({ id: 'STR_TERMS_OF_SERVICE' }),
          to: EXTERNAL_LINKS.TERMS_OF_SERVICE,
          target: '_blank',
          icon: 'tinyExternalLink',
          linkProps: mediumTextLink,
          color: '$onSurfaceTextPrimary',
        },
        {
          text: formatMessage({ id: 'STR_PROVIDE_FEEDBACK' }),
          to: EXTERNAL_LINKS.PROVIDE_FEEDBACK,
          target: '_blank',
          icon: 'tinyExternalLink',
          linkProps: mediumTextLink,
          color: '$onSurfaceTextPrimary',
        },
        {
          text: formatMessage({ id: 'STR_REMOVE_PERSONALIZATION' }),
          onClick: () => {
            setParam(QUERY_PARAM_MODAL_VALUE.REMOVE_PERSONALIZATION);
          },
          color: '$onSurfaceTextPrimary',
          linkProps: mediumTextLink,
        },
      ],
    },
    {
      items: [
        {
          text: formatMessage({ id: 'STR_CLOSE_ACCOUNT' }),
          to: EXTERNAL_LINKS.CLOSE_ACCOUNT,
          linkProps: mediumTextLink,
          color: '$onSurfaceTextPrimary',
        },
      ],
    },
    {
      items: [
        {
          text: formatMessage({ id: 'STR_LOG_OUT' }),
          to: APP_MANAGED_ROUTES.SIGN_OUT,
          color: '$onSurfaceTextCritical',
          linkProps: mediumTextCriticalLink,
        },
      ],
    },
  ];

  return { userSettingsLinks };
}
